import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    orderBy,
    limit,
    Timestamp,
    startAt,
    endAt
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';
import { ProductionEntry, ProductionAggregate } from '../../types/production';
import { isoToEntryDateId } from '../dateUtils';

class ProductionService {
    private readonly SUBCOLLECTION = 'production_entries';

    /**
     * Get aggregated production stats for a list of wells over a date range
     */
    async getAggregatedProduction(wellIds: string[], startDate: Date, endDate: Date): Promise<ProductionAggregate> {
        const companyId = getCompanyId();
        if (!companyId || wellIds.length === 0) {
            return { totalOil: 0, totalGas: 0, totalWater: 0, averageOil: 0, daysCount: 0 };
        }

        let totalOil = 0;
        let totalGas = 0;
        let totalWater = 0;
        let count = 0;

        // Convert dates to YYYY-MM-DD strings for query
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        // Fetch each well's production in parallel
        const promises = wellIds.map(async (wellId) => {
            const ref = collection(db, `companies/${companyId}/wells/${wellId}/${this.SUBCOLLECTION}`);
            // Query using entryDate string
            const q = query(
                ref,
                where('entryDate', '>=', startStr),
                where('entryDate', '<=', endStr)
            );
            
            try {
                const snap = await getDocs(q);
                snap.forEach(doc => {
                    const data = doc.data();
                    // Handle both camelCase and snake_case
                    totalOil += data.oilVolume || data.oil_volume_bbl || 0;
                    totalGas += data.gasVolume || data.gas_volume_mcf || 0;
                    totalWater += data.waterVolume || data.water_volume_bbl || 0;
                    count++;
                });
            } catch (error) {
                console.error(`Failed to fetch production for well ${wellId}`, error);
            }
        });

        await Promise.all(promises);

        return {
            totalOil,
            totalGas,
            totalWater,
            averageOil: count > 0 ? totalOil / count : 0,
            daysCount: count
        };
    }

    /**
     * Submit a daily production report
     * Uses YYYYMMDD format for document ID to enable lexicographic sorting
     */
    async submitReport(entry: Omit<ProductionEntry, 'id' | 'timestamp'>): Promise<string> {
        const companyId = getCompanyId();
        if (!companyId) throw new Error("Tenant ID required");
        if (!entry.wellId) throw new Error("Well ID required for production entry");

        // Convert date to ISO format (YYYY-MM-DD) first
        const entryDate = entry.date instanceof Date 
            ? entry.date.toISOString().split('T')[0] 
            : new Date(entry.date).toISOString().split('T')[0];
        
        // Convert ISO date to YYYYMMDD for document ID
        // This enables lexicographic sorting of entries by date
        const entryId = isoToEntryDateId(entryDate);
        const ref = collection(db, `companies/${companyId}/wells/${entry.wellId}/${this.SUBCOLLECTION}`);
        
        // Import setDoc dynamically or use from module scope if available
        // Since we are in a class, we can't easily import dynamically inside method without async
        // But we can use the imported db and construct doc ref
        const { doc, setDoc } = await import('firebase/firestore');
        const docRef = doc(db, `companies/${companyId}/wells/${entry.wellId}/${this.SUBCOLLECTION}`, entryId);

        await setDoc(docRef, {
            ...entry,
            companyId,
            entryDate, // Store as string for querying
            date: Timestamp.fromDate(entry.date instanceof Date ? entry.date : new Date(entry.date)), // Keep Timestamp for other uses
            timestamp: Timestamp.now(),
            // Map camelCase to snake_case if needed, or just keep camelCase
            // We'll keep camelCase as primary but maybe add snake_case for legacy compat if needed
            oil_volume_bbl: entry.oilVolume,
            gas_volume_mcf: entry.gasVolume,
            water_volume_bbl: entry.waterVolume
        }, { merge: true });

        return entryId;
    }
}

export const productionService = new ProductionService();
